import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, TransferState, inject, makeStateKey } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { Observable, of, shareReplay } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ShopConfig, SHOP_URL_FALLBACK } from '../../_shared/shop-config/shop-url';

/**
 * Konfiguracja adresu sklepu dla kafelka „ZAMOW SUSHI DO DOMU".
 *
 * 🔴 Dlaczego TransferState, a nie samo HttpClient:
 * SSR renderuje kafelek z adresem z bazy; gdyby przegladarka po hydracji
 * strzelala po ten sam adres jeszcze raz, to przez czas tego zapytania
 * trzymalaby WARTOSC POCZATKOWA komponentu (adres awaryjny), czyli href
 * MIGALBY po hydracji — a `curl` widzi wylacznie HTML z SSR i takiego rozjazdu
 * NIE pokazuje. TransferState przenosi odpowiedz w <script> z dokumentu, wiec
 * przegladarka startuje z DOKLADNIE ta sama wartoscia, ktora poszla do HTML-a,
 * i zadnego zapytania nie robi.
 *
 * Istniejacy ServerStateInterceptor nie wystarcza: kluczuje po `req.url`, a ten
 * NA SERWERZE jest absolutny (http://localhost:4000/api/...), zas w przegladarce
 * wzgledny (/api/...) — klucze sie nie spotykaja. Dlatego wlasny, STALY klucz.
 *
 * 🔴 Skad SSR bierze dane (pulapka dv1): przez `environment.apiUrl`, ktory na
 * serwerze wskazuje loopback http://localhost:${PORT}/api — czyli ten sam pod,
 * z pominieciem publicznego hosta. Self-fetch przez host publiczny trafilby na
 * SSO i oddal strone logowania zamiast JSON-a.
 */
export const SHOP_CONFIG_STATE_KEY = makeStateKey<ShopConfig | null>('shopConfig');

@Injectable({ providedIn: 'root' })
export class ShopConfigService {
  private readonly http = inject(HttpClient);
  private readonly transferState = inject(TransferState);
  private readonly isServer = isPlatformServer(inject(PLATFORM_ID));

  private readonly url = environment.apiUrl + '/shop-config';

  /**
   * Strumien z konfiguracja albo `null`, gdy API nie odpowiedzialo. `null` jest
   * wartoscia JAWNA (nie bledem): resolveShopUrl() zamienia je na adres
   * awaryjny, wiec kafelek dziala takze przy padnietej bazie.
   *
   * `shareReplay` + pole: strona glowna i menu mobilne uzywaja tego samego
   * kafelka, a na SSR kazde dodatkowe zapytanie opoznia render.
   */
  private request$?: Observable<ShopConfig | null>;

  load(): Observable<ShopConfig | null> {
    if (this.transferState.hasKey(SHOP_CONFIG_STATE_KEY)) {
      // Przegladarka po hydracji: wartosc z SSR, zero zapytan.
      // hasKey(), a nie get(..., default): `null` jest tu POPRAWNA wartoscia
      // (API padlo na SSR) i musi dac sie odroznic od „klucza nie ma".
      return of(this.transferState.get(SHOP_CONFIG_STATE_KEY, null));
    }
    if (!this.request$) {
      this.request$ = this.http.get<ShopConfig>(this.url).pipe(
        map((config) => this.sanitize(config)),
        catchError(() => of(null)),
        tap((config) => {
          if (this.isServer) {
            this.transferState.set(SHOP_CONFIG_STATE_KEY, config);
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.request$;
  }

  /**
   * Odporny odczyt ciala odpowiedzi: brakujace/niepoprawne `pl` schodzi do
   * adresu awaryjnego, zeby uszkodzony wiersz w bazie nie wygasil kafelka.
   */
  private sanitize(config: ShopConfig | null): ShopConfig | null {
    if (!config || typeof config !== 'object') {
      return null;
    }
    const pl = typeof config.pl === 'string' && config.pl.trim() !== '' ? config.pl.trim() : SHOP_URL_FALLBACK;
    const en = typeof config.en === 'string' && config.en.trim() !== '' ? config.en.trim() : null;
    return { pl, en };
  }
}
